// Public API: classifyPosition(buffer, cursor) → ResolverPositionKind. Pure function.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Position Classifier — core / DML.
//
// Algorithm:
//   1. Bail out to 'string' / 'comment' via lexicalStateAt (state machine
//      mirrored from src/features/sql-grammar/document/scanner.ts — the
//      shipped RecoveryInfo doesn't carry string/comment flags, so we
//      recompute the state directly from the source).
//   2. Resolve the enclosing statement via DocumentStructure.statementAt().
//      If none, scan from the start of the source.
//   3. Dispatch to the DML scanner (scanDmlClause) when the statement kind is
//      insert/update/delete/merge or the leading keyword is WITH, otherwise
//      fall back to the SELECT-oriented scanClause. Both scanners tokenize the
//      statement, track paren depth (so subquery interiors classify
//      independently), and record the last code character before the cursor.
//   4. If the last code character at the cursor is '.', return 'dotted-access'
//      regardless of the enclosing clause.
//   5. Otherwise return the innermost clause state; 'unknown' when no clause
//      keyword has been seen yet.

import type { ParsedBuffer, SqlPosition, ResolverPositionKind } from '../types';
import type { StatementInfo } from '../../sql-grammar/model';
import { lexicalStateAt } from './lexicalState';
import { scanClause } from './clauseScanner';
import { scanDmlClause } from './classifyPosition.dml';
import { dispatchProceduralBodyClassify } from '../procedural/proceduralResolver';

function shouldDispatchToDmlScanner(stmt: StatementInfo | null, source: string, start: number): boolean {
  if (stmt) {
    const k = stmt.kind;
    if (k === 'insert' || k === 'update' || k === 'delete' || k === 'merge') return true;
  }
  // WITH statements are classified as 'select' by coarseKind but their CTE
  // body classification lives in the DML scanner — sniff the leading keyword.
  let i = start;
  while (i < source.length) {
    const c = source[i];
    const c2 = source[i + 1] ?? '';
    if (/\s/.test(c)) { i++; continue; }
    if (c === '-' && c2 === '-') {
      while (i < source.length && source[i] !== '\n') i++;
      continue;
    }
    if (c === '/' && c2 === '*') {
      i += 2;
      while (i < source.length && !(source[i] === '*' && source[i + 1] === '/')) i++;
      if (i < source.length) i += 2;
      continue;
    }
    break;
  }
  const head = source.slice(i, i + 6).toUpperCase();
  return head.startsWith('WITH') || head.startsWith('INSERT') || head.startsWith('UPDATE')
      || head.startsWith('DELETE') || head.startsWith('MERGE');
}

// Returns `ResolverPositionKind<string>` (≡ `string`) for signature stability
// with the pre-plan16-T10 contract, when the procedural-body hand-off could
// dispatch through a per-dialect `ProceduralResolver` registry and produce a
// dialect-specific arm (e.g. `'tsql-variable'`, `'plpgsql-trigger-var'`) that
// doesn't appear in `ResolverPositionKindCore`. That registry was dead code
// (every producer passed an empty attachments array) and was deleted at
// plan16 T10 — the hand-off below now only ever yields the load-bearing
// `'procedural-body'` sentinel (or null; see proceduralResolver.ts).
// Engine-agnostic core / DML paths still return the narrow `ResolverPositionKind`
// (core) arms — the widening here only documents that the seam tolerates any
// dialect's narrower union, kept in case a future dialect-specific return
// value is wired through this seam again. Per ROUND2-I9 phase 2 (open for
// extension).
export function classifyPosition(buffer: ParsedBuffer, cursor: SqlPosition): ResolverPositionKind<string> {
  const { parsedDocument, documentStructure } = buffer;
  const { source, options } = parsedDocument;
  const offset = Math.min(Math.max(cursor.offset, 0), source.length);
  const clampedCursor: SqlPosition = offset === cursor.offset ? cursor : { ...cursor, offset };

  // Procedural-body hand-off first — inside a procedural body the outer
  // source may look like a PG $$...$$ string or a T-SQL BEGIN…END block,
  // both of which would mis-classify via the outer scanners. The sub-
  // resolver (or the 'procedural-body' outer-boundary value) wins.
  const procedural = dispatchProceduralBodyClassify(buffer, clampedCursor);
  if (procedural !== null) return procedural;

  const lex = lexicalStateAt(source, offset, options);
  if (lex === 'string') return 'string';
  if (lex === 'comment') return 'comment';

  const stmt = documentStructure.statementAt(offset);
  const start = stmt ? stmt.range.startOffset : 0;
  const dispatch = shouldDispatchToDmlScanner(stmt, source, start);
  const { clause, lastCodeChar } = dispatch
    ? scanDmlClause(source, start, offset, options)
    : scanClause(source, start, offset, options);

  if (lastCodeChar === '.') return 'dotted-access';
  return clause;
}
